"use client";

import { useAuth } from "@/components/features/auth/hooks/use-auth";
import { routes } from "@/lib/routes";
import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { LifeBuoy, LogIn, LogOut } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { AccountSwitcher } from "../../account-switcher";
import { useAccountNavItems } from "../hooks/use-nav-items";
import { MobileNavMenuProps } from "../types";
import { DropdownNavItem } from "./nav-item-renderers";

function MobileMenuIcon() {
  return (
    <svg
      className="size-[1.5rem!important]"
      viewBox="0 0 30 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.11111 18.8095H23.8889M6.11111 11.1905H23.8889"
        stroke="#09090B"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function MobileNavMenu({ className, mainNavItems }: MobileNavMenuProps) {
  const { user, isAuthenticated } = useAuth();
  const [accountSwitcherOpen, setAccountSwitcherOpen] = useState(false);

  const accountNavItems = useAccountNavItems(setAccountSwitcherOpen);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className={className} asChild>
          <Button variant="ghost" size="icon">
            <MobileMenuIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-background/50 w-56 backdrop-blur-xl">
          {mainNavItems.map((item) => (
            <DropdownNavItem key={item.name} item={item} />
          ))}
          <DropdownMenuSeparator />

          {isAuthenticated ? (
            <>
              <DropdownMenuGroup>
                <DropdownMenuLabel>
                  {user?.displayName}{" "}
                  {/* TODO: Add org name with async getActiveOrganization() */}
                </DropdownMenuLabel>
                {accountNavItems.map((item) => (
                  <DropdownNavItem key={item.name} item={item} />
                ))}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href={routes.support}>
                  <LifeBuoy className="h-4 w-4" />
                  Support
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href={routes.auth.signOut}>
                  <LogOut className="h-4 w-4" />
                  Logout
                </Link>
              </DropdownMenuItem>
            </>
          ) : (
            <>
              <DropdownMenuItem asChild>
                <Link href={routes.support}>
                  <LifeBuoy className="h-4 w-4" />
                  Support
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href={routes.auth.signIn}>
                  <LogIn className="h-4 w-4" />
                  Login
                </Link>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {isAuthenticated && (
        <AccountSwitcher
          open={accountSwitcherOpen}
          onOpenChange={setAccountSwitcherOpen}
        />
      )}
    </>
  );
}
